import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Badge,
  Divider,
} from '@fluentui/react-components';
import {
  PersonRegular,
  KeyRegular,
  AppsRegular,
  DesktopRegular,
  NoteRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
} from '@fluentui/react-icons';
import { OnboardingFormData } from '../../../hooks/useOnboardingForm';
import { apiClient, ListResponse } from '../../../api/client';
import { DATE_FORMAT } from '../../../config/constants';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  intro: {
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalM,
  },
  section: {
    marginBottom: tokens.spacingVerticalL,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXL}`,
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  value: {
    fontWeight: tokens.fontWeightSemibold,
  },
  accessList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  accessItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
  },
  accessEnabled: {
    backgroundColor: '#e6f4ea',
    color: '#137333',
  },
  accessDisabled: {
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground3,
  },
  notes: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    whiteSpace: 'pre-wrap',
  },
});

interface ReviewStepProps {
  formData: OnboardingFormData;
}

interface Software {
  _id: string;
  name: string;
}

export function ReviewStep({ formData }: ReviewStepProps) {
  const styles = useStyles();
  const [softwareMap, setSoftwareMap] = useState<Record<string, string>>({});

  useEffect(() => {
    apiClient
      .get<ListResponse<Software>>('/software')
      .then((response) => {
        const map: Record<string, string> = {};
        response.data.forEach((sw) => {
          map[sw._id] = sw.name;
        });
        setSoftwareMap(map);
      })
      .catch(console.error);
  }, []);

  const formatDate = (date: Date | null) => {
    if (!date) return 'Not specified';
    return date.toLocaleDateString('en-US', DATE_FORMAT);
  };

  const accessItems = [
    { key: 'vpn', label: 'VPN' },
    { key: 'hrSystem', label: 'HR System' },
    { key: 'hubSpot', label: 'HubSpot' },
    { key: 'fileServer', label: 'FileServer' },
    { key: 'sage', label: 'SAGE' },
  ] as const;

  const peripherals = [
    formData.hardware.primaryDisplay && 'Primary Display',
    formData.hardware.secondaryDisplay && 'Secondary Display',
    formData.hardware.mouse && 'Mouse',
    formData.hardware.keyboard && 'Keyboard',
    formData.hardware.laptopBag && 'Laptop Bag',
  ].filter(Boolean);

  return (
    <div className={styles.container}>
      <Text className={styles.intro}>
        Please review the information below before submitting the onboarding request.
      </Text>

      {/* User Details */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <PersonRegular /> User Details
        </div>
        <div className={styles.grid}>
          <div className={styles.item}>
            <Text className={styles.label}>Name</Text>
            <Text className={styles.value}>{formData.displayName || `${formData.firstName} ${formData.lastName}`}</Text>
          </div>
          <div className={styles.item}>
            <Text className={styles.label}>Role</Text>
            <Text className={styles.value}>
              <Badge appearance="outline">{formData.role}</Badge>
            </Text>
          </div>
          <div className={styles.item}>
            <Text className={styles.label}>Job Title</Text>
            <Text className={styles.value}>{formData.jobTitle}</Text>
          </div>
          <div className={styles.item}>
            <Text className={styles.label}>Department</Text>
            <Text className={styles.value}>{formData.department}</Text>
          </div>
          <div className={styles.item}>
            <Text className={styles.label}>Site</Text>
            <Text className={styles.value}>{formData.siteCode}</Text>
          </div>
          <div className={styles.item}>
            <Text className={styles.label}>Line Manager</Text>
            <Text className={styles.value}>{formData.lineManagerName}</Text>
          </div>
          <div className={styles.item}>
            <Text className={styles.label}>Start Date</Text>
            <Text className={styles.value}>{formatDate(formData.startDate)}</Text>
          </div>
          <div className={styles.item}>
            <Text className={styles.label}>End Date</Text>
            <Text className={styles.value}>{formatDate(formData.endDate)}</Text>
          </div>
          {formData.mobilePhone && (
            <div className={styles.item}>
              <Text className={styles.label}>Mobile Phone</Text>
              <Text className={styles.value}>{formData.mobilePhone}</Text>
            </div>
          )}
          {formData.businessPhone && (
            <div className={styles.item}>
              <Text className={styles.label}>Business Phone</Text>
              <Text className={styles.value}>{formData.businessPhone}</Text>
            </div>
          )}
        </div>
      </div>

      <Divider />

      {/* Access & Provisioning */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <KeyRegular /> Access & Provisioning
        </div>
        <div className={styles.accessList}>
          {accessItems.map((item) => {
            const enabled = formData.accessProvisioning[item.key];
            return (
              <span
                key={item.key}
                className={`${styles.accessItem} ${enabled ? styles.accessEnabled : styles.accessDisabled}`}
              >
                {enabled ? <CheckmarkCircleRegular /> : <DismissCircleRegular />}
                {item.label}
              </span>
            );
          })}
        </div>
      </div>

      <Divider />

      {/* Software */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <AppsRegular /> Additional Software
        </div>
        {formData.additionalSoftware.length > 0 ? (
          <div className={styles.accessList}>
            {formData.additionalSoftware.map((id) => (
              <Badge key={id} appearance="filled" color="informative">
                {softwareMap[id] || id}
              </Badge>
            ))}
          </div>
        ) : (
          <Text style={{ color: tokens.colorNeutralForeground3 }}>
            No additional software requested
          </Text>
        )}
      </div>

      <Divider />

      {/* Hardware */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <DesktopRegular /> Hardware
        </div>
        <div className={styles.grid}>
          <div className={styles.item}>
            <Text className={styles.label}>PC Type</Text>
            <Text className={styles.value}>
              {formData.hardware.isReused ? 'Reused PC' : 'New PC'}
            </Text>
          </div>
          {formData.hardware.isReused ? (
            <div className={styles.item}>
              <Text className={styles.label}>Service Tag</Text>
              <Text className={styles.value}>{formData.hardware.serviceTag}</Text>
            </div>
          ) : (
            <>
              <div className={styles.item}>
                <Text className={styles.label}>Device Type</Text>
                <Text className={styles.value}>{formData.hardware.type}</Text>
              </div>
              <div className={styles.item}>
                <Text className={styles.label}>Peripherals</Text>
                <Text className={styles.value}>
                  {peripherals.length > 0 ? peripherals.join(', ') : 'None'}
                </Text>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Additional Notes */}
      {formData.additionalRequests && (
        <>
          <Divider />
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <NoteRegular /> Additional Notes
            </div>
            <div className={styles.notes}>{formData.additionalRequests}</div>
          </div>
        </>
      )}
    </div>
  );
}
